import {
  AddAPhoto as AddAPhotoIcon,
  ArrowDropDown as ArrowDropDownIcon,
  CloudUpload as CloudUploadIcon,
  Collections as CollectionsIcon,
  Delete as DeleteIcon,
  InsertDriveFile as FileIcon,
} from '@mui/icons-material';
import {
  Avatar,
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { Field, useFormikContext } from 'formik';
import { camelCase, get } from 'lodash';
import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { InputComponent } from '..';
import { useToastr } from '../../toastr/toastr-context';
import { ImageCropDialog } from '../image-crop/image-crop-dialog';

export interface InputFileUploadProps extends Omit<InputComponent, 'size'> {
  /**
   * Visual variant. `input` shows a preview + upload button group; `avatar`
   * shows a clickable rounded avatar with a caption underneath.
   */
  variant?: 'input' | 'avatar';

  /** Comma-separated MIME types passed to the underlying `<input type="file">`. */
  accept?: string;

  multiple?: boolean;

  /** Maximum allowed file size in bytes. Oversized files are rejected with a toast. */
  maxSizeBytes?: number;

  /**
   * Optional async upload handler. When provided, the picked File is passed
   * here and the returned URL is stored in the form field. When omitted, the
   * File object itself is stored in the form field.
   */
  onUpload?: (file: File) => Promise<string>;

  /** Called after the form value is updated (post-upload if `onUpload` is set). */
  onChange?: (name: string, value?: File | File[] | string) => void;

  /**
   * When provided, the menu gains a "Choose from library" option that invokes
   * this handler. The caller is responsible for rendering whatever picker UI
   * is appropriate and writing the result into the form field.
   */
  onLibraryClick?: () => void;

  /** Menu label for the library option. */
  libraryLabel?: string;

  /** Button label when no file is set. */
  uploadLabel?: string;

  /** Button label when a file is already set. */
  replaceLabel?: string;

  /** Button label shown while an upload is in progress. */
  uploadingLabel?: string;

  /** Avatar diameter in pixels. Only used when `variant='avatar'`. */
  avatarSize?: number;

  /**
   * When set, single-file image picks open a crop dialog at this aspect ratio
   *  (width:height) before upload. The cropped result is what gets uploaded.
   */
  cropAspect?: number;
  /** Crop overlay shape for the dialog (default 'rect'). */
  cropShape?: 'rect' | 'round';
}

function defaultUploadLabel(
  uploading: boolean,
  hasValue: boolean,
  base: string,
  replace: string,
  busy: string,
): string {
  if (uploading) return busy;
  if (hasValue) return replace;
  return base;
}

function valueToPreviewUrl(value: unknown): string | undefined {
  if (!value) return undefined;
  if (typeof value === 'string') return value;
  if (value instanceof File) return URL.createObjectURL(value);
  if (Array.isArray(value) && value[0] instanceof File)
    return URL.createObjectURL(value[0] as File);
  return undefined;
}

function PreviewImage({ url }: { url: string }) {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <Box
        sx={{
          width: 240,
          height: 120,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'text.secondary',
        }}
      >
        <FileIcon />
      </Box>
    );
  }
  return (
    <Box
      component="img"
      src={url}
      alt="Preview"
      onError={() => setFailed(true)}
      sx={{
        maxHeight: 120,
        maxWidth: 240,
        display: 'block',
        objectFit: 'contain',
      }}
    />
  );
}

export function InputFileUpload({
  label,
  disabled,
  hidden,
  required,
  className,
  hint,
  nameOverride,
  showLabel = !hidden,
  readOnly = false,
  variant = 'input',
  accept = undefined,
  multiple = false,
  maxSizeBytes = undefined,
  onUpload = undefined,
  onChange = undefined,
  onLibraryClick = undefined,
  libraryLabel = 'Choose from library',
  uploadLabel = 'Upload File',
  replaceLabel = 'Replace File',
  uploadingLabel = 'Uploading…',
  avatarSize = 92,
  cropAspect = undefined,
  cropShape = undefined,
}: InputFileUploadProps) {
  const { values, errors, setFieldValue } =
    useFormikContext<Record<string, unknown>>();
  const { push } = useToastr();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [cropSrc, setCropSrc] = useState<string | null>(null);

  // Revoke the crop object URL if we unmount while the dialog is still open
  // (closeCrop covers cancel/confirm, but not an unmount mid-crop).
  useEffect(() => {
    if (!cropSrc) return undefined;
    return () => URL.revokeObjectURL(cropSrc);
  }, [cropSrc]);

  const name = useMemo(
    () => nameOverride ?? camelCase(label),
    [label, nameOverride],
  );

  const error = useMemo(
    () => get(errors, name) as string | undefined,
    [errors, name],
  );

  const currentValue = get(values, name);
  let hasValue = !!currentValue;
  if (hasValue) {
    if (typeof currentValue === 'string') {
      hasValue = currentValue.length > 0;
    } else if (Array.isArray(currentValue)) {
      hasValue = currentValue.length > 0;
    }
  }

  // Object URLs created for File previews need explicit revocation.
  const previewUrl = useMemo(
    () => valueToPreviewUrl(currentValue),
    [currentValue],
  );
  useEffect(() => {
    if (
      previewUrl &&
      (currentValue instanceof File ||
        (Array.isArray(currentValue) && currentValue[0] instanceof File))
    ) {
      const url = previewUrl;
      return () => URL.revokeObjectURL(url);
    }
    return undefined;
  }, [previewUrl, currentValue]);

  const validate = useCallback(
    (value: unknown) => {
      if (required && !value) {
        return `${label ?? 'File'} is required.`;
      }
      return undefined;
    },
    [label, required],
  );

  const handleSingleFile = useCallback(
    async (file: File) => {
      if (maxSizeBytes && file.size > maxSizeBytes) {
        const mb = (maxSizeBytes / (1024 * 1024)).toFixed(0);
        push({
          title: 'File too large',
          body: `Files must be under ${mb} MB.`,
          bg: 'danger',
        });
        return;
      }

      if (onUpload) {
        setUploading(true);
        try {
          const url = await onUpload(file);
          await setFieldValue(name, url);
          onChange?.(name, url);
        } catch (err) {
          push({
            title: 'Upload failed',
            body:
              err instanceof Error ? err.message : 'An unknown error occurred.',
            bg: 'danger',
          });
        } finally {
          setUploading(false);
        }
      } else {
        await setFieldValue(name, file);
        onChange?.(name, file);
      }
    },
    [maxSizeBytes, name, onChange, onUpload, push, setFieldValue],
  );

  const handleFileChange = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(event.target.files ?? []);
      // Reset so the same file can be re-selected after a clear
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (files.length === 0) return;

      if (multiple) {
        if (maxSizeBytes) {
          const oversized = files.find((f) => f.size > maxSizeBytes);
          if (oversized) {
            const mb = (maxSizeBytes / (1024 * 1024)).toFixed(0);
            push({
              title: 'File too large',
              body: `Files must be under ${mb} MB.`,
              bg: 'danger',
            });
            return;
          }
        }
        await setFieldValue(name, files);
        onChange?.(name, files);
        return;
      }

      // Crop-enabled single-file pick: open the dialog instead of uploading now.
      if (cropAspect != null && !multiple) {
        setCropSrc(URL.createObjectURL(files[0]));
        return;
      }

      await handleSingleFile(files[0]);
    },
    [
      cropAspect,
      handleSingleFile,
      maxSizeBytes,
      multiple,
      name,
      onChange,
      push,
      setFieldValue,
    ],
  );

  const closeCrop = useCallback(() => {
    setCropSrc((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  }, []);

  const handleCropped = useCallback(
    async (blob: Blob) => {
      const ext = blob.type === 'image/png' ? 'png' : 'jpg';
      const file = new File([blob], `crop.${ext}`, {
        type: blob.type || 'image/jpeg',
      });
      closeCrop();
      await handleSingleFile(file);
    },
    [closeCrop, handleSingleFile],
  );

  const triggerUpload = () => fileInputRef.current?.click();

  const handleRemove = useCallback(async () => {
    await setFieldValue(name, multiple ? [] : '');
    onChange?.(name, multiple ? [] : undefined);
  }, [multiple, name, onChange, setFieldValue]);

  const openMenu = (e: React.MouseEvent<HTMLElement>) =>
    setMenuAnchor(e.currentTarget);
  const closeMenu = () => setMenuAnchor(null);

  const hasLibrary = !!onLibraryClick;

  const hiddenInput = (
    <input
      ref={fileInputRef}
      type="file"
      accept={accept}
      multiple={multiple}
      style={{ display: 'none' }}
      onChange={handleFileChange}
      disabled={disabled || readOnly}
      name={name}
    />
  );

  const menu = (
    <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
      <MenuItem
        onClick={() => {
          closeMenu();
          triggerUpload();
        }}
      >
        <CloudUploadIcon fontSize="small" sx={{ mr: 1 }} />
        Upload new file
      </MenuItem>
      {hasLibrary && (
        <MenuItem
          onClick={() => {
            closeMenu();
            onLibraryClick?.();
          }}
        >
          <CollectionsIcon fontSize="small" sx={{ mr: 1 }} />
          {libraryLabel}
        </MenuItem>
      )}
    </Menu>
  );

  const cropDialog =
    cropSrc != null && cropAspect != null ? (
      <ImageCropDialog
        open
        imageSrc={cropSrc}
        aspect={cropAspect}
        cropShape={cropShape}
        onCancel={closeCrop}
        onCropped={handleCropped}
      />
    ) : null;

  if (hidden) return null;

  // --- Avatar variant ---------------------------------------------------
  if (variant === 'avatar') {
    const handleAvatarClick = (e: React.MouseEvent<HTMLElement>) => {
      if (disabled || readOnly) return;
      if (hasLibrary) {
        openMenu(e);
      } else {
        triggerUpload();
      }
    };

    return (
      <Field name={name} validate={validate}>
        {() => (
          <Box
            className={className}
            sx={{
              display: 'inline-flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            {hiddenInput}
            <Tooltip title={hasValue ? 'Change file' : 'Add file'}>
              <Box
                sx={{
                  position: 'relative',
                  cursor: disabled || readOnly ? 'default' : 'pointer',
                  display: 'inline-flex',
                  opacity: disabled ? 0.5 : 1,
                }}
                onClick={handleAvatarClick}
              >
                <Avatar
                  src={previewUrl}
                  sx={{
                    width: avatarSize,
                    height: avatarSize,
                    bgcolor: 'action.hover',
                  }}
                  variant="rounded"
                >
                  {uploading ? (
                    <CircularProgress size={24} />
                  ) : (
                    !previewUrl && (
                      <AddAPhotoIcon sx={{ color: 'text.secondary' }} />
                    )
                  )}
                </Avatar>
                {hasValue && !uploading && !disabled && !readOnly && (
                  <IconButton
                    size="small"
                    aria-label="Remove file"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemove();
                    }}
                    sx={{
                      position: 'absolute',
                      top: -6,
                      right: -6,
                      width: 20,
                      height: 20,
                      bgcolor: 'background.paper',
                      border: 1,
                      borderColor: 'divider',
                      '&:hover': {
                        bgcolor: 'error.light',
                        color: 'error.contrastText',
                      },
                    }}
                  >
                    <DeleteIcon sx={{ fontSize: 12 }} />
                  </IconButton>
                )}
              </Box>
            </Tooltip>
            {showLabel && label && (
              <Typography variant="caption" color="text.secondary">
                {label}
                {required ? ' *' : ''}
              </Typography>
            )}
            {(error || hint) && (
              <Typography
                variant="caption"
                color={error ? 'error' : 'text.secondary'}
              >
                {error ?? hint}
              </Typography>
            )}
            {menu}
            {cropDialog}
          </Box>
        )}
      </Field>
    );
  }

  // --- Input variant ----------------------------------------------------
  return (
    <Field name={name} validate={validate}>
      {() => (
        <Stack spacing={1} className={className}>
          {showLabel && label && (
            <Typography variant="subtitle2" color="text.secondary">
              {label}
              {required ? ' *' : ''}
            </Typography>
          )}

          {previewUrl && (
            <Box
              sx={{
                position: 'relative',
                display: 'inline-flex',
                alignSelf: 'flex-start',
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                overflow: 'hidden',
                bgcolor: 'background.paper',
              }}
            >
              {/* Best-effort image preview — falls back to a generic file icon on error. */}
              <PreviewImage url={previewUrl} />
              {!disabled && !readOnly && (
                <IconButton
                  size="small"
                  aria-label="Remove file"
                  onClick={() => handleRemove()}
                  sx={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    bgcolor: 'background.paper',
                    '&:hover': {
                      bgcolor: 'error.light',
                      color: 'error.contrastText',
                    },
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          )}

          <Box>
            {hiddenInput}
            <ButtonGroup
              variant="outlined"
              size="small"
              disabled={uploading || disabled || readOnly}
            >
              <Button
                startIcon={
                  uploading ? (
                    <CircularProgress size={14} />
                  ) : (
                    <CloudUploadIcon />
                  )
                }
                onClick={triggerUpload}
              >
                {defaultUploadLabel(
                  uploading,
                  hasValue,
                  uploadLabel,
                  replaceLabel,
                  uploadingLabel,
                )}
              </Button>
              {hasLibrary && (
                <Button
                  aria-label="More file options"
                  onClick={openMenu}
                  sx={{ px: 0.5 }}
                >
                  <ArrowDropDownIcon fontSize="small" />
                </Button>
              )}
            </ButtonGroup>
            {menu}
            {cropDialog}
          </Box>

          {(error || hint) && (
            <Typography
              variant="caption"
              color={error ? 'error' : 'text.secondary'}
            >
              {error ?? hint}
            </Typography>
          )}
        </Stack>
      )}
    </Field>
  );
}
